import React, {
    PropsWithChildren,
    useState,
    createContext,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { useAuthContext } from 'utils/contexts/AuthContext';
import { AuthState } from 'utils/contexts/AuthContext/AuthContextType';
import { useSiteContext } from 'utils/contexts/SiteContext';
import { useGet } from 'utils/hooks/useAPI';

export type HINT = string;
export type FEATURE = string;
export type FLAG = string;

export enum HintType {
    global = 'hasGlobalHint',
    site = 'hasSiteHint',
}

export type GlobalHints = {
    hints: HINT[];
    features: FEATURE[];
    flags: {
        [flag: FLAG]: unknown;
    };
};

export type SiteHints = {
    hints: HINT[];
};

export interface HintContextType {
    globalHints?: GlobalHints;
    siteHints?: SiteHints;
    hasGlobalHint: (hint: HINT) => boolean;
    hasSiteHint: (hint: HINT) => boolean;
    hasFeature: (hint: HINT) => boolean;
    getFlag: <T = unknown>(flag: FLAG, defaultValue: T) => T;
    loading: boolean;
}

export const HINTS_API = '/helios-api/hints/';

// Re not-null assertion: See null check in useHintContext hook
export const HintContext = createContext<HintContextType>(null!);

export const HintProvider = ({ children }: PropsWithChildren) => {
    const { authState } = useAuthContext();
    const { site } = useSiteContext();

    const [globalHints, setGlobalHints] = useState<GlobalHints>();
    const [siteHints, setSiteHints] = useState<SiteHints>();

    const [, loadGlobalHints] = useGet<GlobalHints>(HINTS_API, true);
    const [, loadSiteHints] = useGet<SiteHints>(
        `${HINTS_API}sites/${site?.site_id}`,
        true
    );

    useEffect(() => {
        setGlobalHints(undefined);
        if (authState === AuthState.AUTHENTICATED) {
            loadGlobalHints().then(setGlobalHints);
        }
    }, [authState, loadGlobalHints]);

    useEffect(() => {
        if (site?.site_id) {
            setSiteHints(undefined);
            loadSiteHints().then(setSiteHints);
        } else {
            setSiteHints({ hints: [] });
        }
    }, [site?.site_id, loadSiteHints]);

    const features: FEATURE[] = useMemo(
        () => globalHints?.features || [],
        [globalHints?.features]
    );

    const hasFeature = useCallback(
        (feature: FEATURE) => features.includes(feature),
        [features]
    );
    const hasGlobalHint = useCallback(
        (hint: HINT) => globalHints?.hints.includes(hint) || false,
        [globalHints?.hints]
    );
    const hasSiteHint = useCallback(
        (hint: HINT) => siteHints?.hints.includes(hint) || false,
        [siteHints?.hints]
    );
    const getFlag = useCallback(
        <T = unknown,>(flag: FLAG, defaultValue: T) => {
            if (
                !globalHints?.flags ||
                !Object.hasOwn(globalHints.flags, flag)
            ) {
                return defaultValue;
            }

            return globalHints.flags[flag] as T;
        },
        [globalHints?.flags]
    );

    return (
        <HintContext
            value={{
                globalHints,
                siteHints,
                hasGlobalHint,
                hasSiteHint,
                hasFeature,
                getFlag,
                loading: !globalHints || !siteHints,
            }}
        >
            {children}
        </HintContext>
    );
};

export const useHintContext = () => {
    const hintContext = React.useContext(HintContext);

    if (!hintContext) {
        throw new Error('useHintContext must be used within a HintProvider');
    }

    return hintContext;
};
